import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import ResearchResultPage from './ResearchResultPage'
import { generateResearchPaper } from './researchUtils'
import { MODEL_MODES } from "../../constants/agentConfig"
import { AgentHistoryIntegration } from '../../utils/agentHistoryIntegration'
import { useCreditWarning } from '../../hooks/useCreditWarning'
import CreditWarningModal from '../../components/CreditWarningModal'
import { useSubscription } from '../../hooks/useSubscription'
import { AGENT_TYPES } from '../index.js'
import linkupService from '../../services/linkupService'

// Research Agent States
const AGENT_STATES = {
  GENERATING: 'generating',
  COMPLETED: 'completed'
}

const ResearchAgent = ({ query, onBack, llmService }) => {
  const [currentState, setCurrentState] = useState(AGENT_STATES.GENERATING)
  const [researchPaper, setResearchPaper] = useState({})
  const [academicSources, setAcademicSources] = useState([])
  const [relevantImages, setRelevantImages] = useState([])
  const [progress, setProgress] = useState({ current: 0, total: 0 })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [hasInitialized, setHasInitialized] = useState(false)
  const { warningModal, checkAndWarnCredits, closeWarning } = useCreditWarning()
  const { consumeCredits } = useSubscription()
  
  // Use refs to track state and prevent unnecessary re-generation
  const isGeneratingPaper = useRef(false)
  const currentQuery = useRef(query)
  const hasEverGenerated = useRef(false)

  // Session storage keys for persistence
  const getSessionKey = (suffix) => `research_agent_${query}_${suffix}`

  // Load persisted state on component mount
  useEffect(() => {
    const loadPersistedState = () => {
      try {
        const persistedState = sessionStorage.getItem(getSessionKey('state'))
        const persistedPaper = sessionStorage.getItem(getSessionKey('paper'))
        const persistedSources = sessionStorage.getItem(getSessionKey('sources'))
        
        if (persistedState && persistedPaper) {
          const state = JSON.parse(persistedState)
          const paperData = JSON.parse(persistedPaper)
          
          console.log('📋 Loading persisted Research state:', state.currentState)
          setCurrentState(state.currentState)
          setResearchPaper(paperData)
          setHasInitialized(true)
          hasEverGenerated.current = true
          
          if (persistedSources) {
            const sourcesData = JSON.parse(persistedSources)
            setAcademicSources(sourcesData)
          }
          
          const persistedImages = sessionStorage.getItem(getSessionKey('images'))
          if (persistedImages) {
            const imagesData = JSON.parse(persistedImages)
            setRelevantImages(imagesData)
          }
          
          // Don't generate paper if we have persisted state
          return true
        }
      } catch (error) {
        console.warn('⚠️ Failed to load persisted state:', error)
        // Clear corrupted data
        try {
          sessionStorage.removeItem(getSessionKey('state'))
          sessionStorage.removeItem(getSessionKey('paper'))
          sessionStorage.removeItem(getSessionKey('sources'))
          sessionStorage.removeItem(getSessionKey('images'))
        } catch (clearError) {
          console.warn('⚠️ Failed to clear corrupted session data:', clearError)
        }
      }
      return false
    }
    
    // Only load persisted state if query matches current query
    if (currentQuery.current === query) {
      const hasPersistedState = loadPersistedState()
      if (hasPersistedState) {
        return // Skip paper generation
      }
    } else {
      // Query changed, clear old session data
      try {
        const oldQuery = currentQuery.current
        sessionStorage.removeItem(`research_agent_${oldQuery}_state`)
        sessionStorage.removeItem(`research_agent_${oldQuery}_paper`)
        sessionStorage.removeItem(`research_agent_${oldQuery}_sources`)
        sessionStorage.removeItem(`research_agent_${oldQuery}_images`)
        sessionStorage.removeItem(`research_generation_${oldQuery}`)
      } catch (error) {
        console.warn('⚠️ Failed to clear old session data:', error)
      }
      currentQuery.current = query
    }
    
    // Generate research paper only if needed
    if (query && !hasInitialized && !isGeneratingPaper.current && !hasEverGenerated.current) {
      generateInitialPaper()
    }
  }, [query])

  // Persist state changes to session storage
  useEffect(() => {
    if (hasInitialized && Object.keys(researchPaper).length > 0) {
      try {
        sessionStorage.setItem(getSessionKey('state'), JSON.stringify({
          currentState,
          hasInitialized: true
        }))
        sessionStorage.setItem(getSessionKey('paper'), JSON.stringify(researchPaper))
        
        if (academicSources.length > 0) {
          sessionStorage.setItem(getSessionKey('sources'), JSON.stringify(academicSources))
        }
        
        if (relevantImages.length > 0) {
          sessionStorage.setItem(getSessionKey('images'), JSON.stringify(relevantImages))
        }
      } catch (error) {
        console.warn('⚠️ Failed to persist state:', error)
      }
    }
  }, [currentState, researchPaper, academicSources, relevantImages, hasInitialized, query])

  const generateInitialPaper = async () => {
    if (isGeneratingPaper.current || hasEverGenerated.current) {
      console.log('⚠️ Paper generation already in progress or completed, skipping...')
      return
    }
    
    // Additional protection against React StrictMode double execution
    const generationKey = `research_generation_${query}`
    const existingGeneration = sessionStorage.getItem(generationKey)
    if (existingGeneration) {
      console.log('⚠️ Generation already initiated for this query, skipping...')
      return
    }
    sessionStorage.setItem(generationKey, 'true')
    
    // Check credits before starting generation
    const canProceed = await checkAndWarnCredits(AGENT_TYPES.RESEARCH)
    if (!canProceed) {
      sessionStorage.removeItem(generationKey)
      return // Stop execution if insufficient credits
    }
    
    isGeneratingPaper.current = true
    
    try {
      setIsLoading(true)
      setError(null)
      setCurrentState(AGENT_STATES.GENERATING)
      console.log('🔄 Generating research paper for:', query)
      
      const result = await generateResearchPaper(query, llmService, setProgress)
      setResearchPaper(result.researchPaper)
      setAcademicSources(result.academicSources)
      setRelevantImages(result.relevantImages || [])
      setHasInitialized(true)
      hasEverGenerated.current = true
      setCurrentState(AGENT_STATES.COMPLETED)
      
      // Consume credits after successful generation
      await consumeCredits(AGENT_TYPES.RESEARCH)
      
      // Save to agent history
      try {
        const saveResult = await AgentHistoryIntegration.saveResearchResult({
          query: query,
          researchPaper: result.researchPaper,
          academicSources: result.academicSources,
          relevantImages: result.relevantImages || [],
          progress: { current: 100, total: 100 }
        })
        
        if (saveResult.success) {
          console.log('✅ Research paper berhasil disimpan ke history:', saveResult.data)
        } else {
          console.warn('⚠️ Gagal menyimpan research paper ke history:', saveResult.error)
        }
      } catch (historyError) {
        console.warn('⚠️ Failed to save to agent history:', historyError)
      }
      
      console.log('✅ Research paper generation completed successfully')
    } catch (error) {
      console.error('❌ Error generating research paper:', error)
      setError('Gagal membuat research paper')
      setHasInitialized(false)
      hasEverGenerated.current = false // Allow retry on error
      setCurrentState(AGENT_STATES.GENERATING)
    } finally {
      setIsLoading(false)
      isGeneratingPaper.current = false
      // Clean up generation key
      const generationKey = `research_generation_${query}`
      sessionStorage.removeItem(generationKey)
    }
  }

  // Render based on current state
  switch (currentState) {
    case AGENT_STATES.GENERATING:
      return (
        <>          <ResearchResultPage            result={{              researchPaper,              academicSources,              relevantImages            }}            query={query}            progress={progress}            onBack={onBack}            isLoading={isLoading}            error={error}            isGenerating={true}          />
          {warningModal.isOpen && (
            <CreditWarningModal
              isOpen={warningModal.isOpen}
              onClose={closeWarning}
              onConfirm={warningModal.onConfirm}
              requiredCredits={warningModal.requiredCredits}
              currentCredits={warningModal.currentCredits}
              agentName="Research Agent"
            />
          )}
        </>
      )
    
    case AGENT_STATES.COMPLETED:
      return (
        <>          <ResearchResultPage            result={{              researchPaper,              academicSources,              relevantImages            }}            query={query}            progress={progress}            onBack={onBack}            isLoading={false}            error={error}            isGenerating={false}          />
          {warningModal.isOpen && (
            <CreditWarningModal
              isOpen={warningModal.isOpen}
              onClose={closeWarning}
              onConfirm={warningModal.onConfirm}
              requiredCredits={warningModal.requiredCredits}
              currentCredits={warningModal.currentCredits}
              agentName="Research Agent"
            />
          )}
        </>
      )
    
    default:
      return null
  }
}

ResearchAgent.propTypes = {
  query: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  llmService: PropTypes.object.isRequired
}

export default ResearchAgent